import { Input } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components/macro'
import FoodSafetyVisuallyHidden from 'src/components/food-safety/FoodSafetyVisuallyHidden'
import {
  FOOD_SAFETY_RECHECK_REMINDER_MINUTES,
  FOOD_SAFETY_SECONDARY_TEXT,
} from 'src/constants/foodSafety'
import {
  FOOD_SAFETY_CORRECTIVE_NOTE_MAX_LENGTH,
  focusFoodSafetyCorrectiveField,
  foodSafetyCorrectiveAnnouncement,
  foodSafetyCorrectiveActionOptions,
  foodSafetyCorrectiveNotePlaceholder,
  foodSafetyCorrectiveNoteRequired,
  foodSafetyRecheckWording,
  formatFoodSafetyOutOfRangeHeadline,
  formatFoodSafetyRejectedHeadline,
  isFoodSafetyTempOutsideRange,
  parseFoodSafetyRecheckValue,
  resolveFoodSafetyCorrectiveFocusField,
} from 'src/utils/foodSafetyCorrectiveAction'
import { FOOD_SAFETY_SUBMIT_WITHOUT_ACTION_LABEL } from 'src/utils/foodSafetyFeedback'
import { FOOD_SAFETY_TEMP_TONE_COLOUR } from 'src/utils/foodSafetyTempColour'

/**
 * One corrective-action panel for every section: what went wrong, what was
 * done (chips + note), and an optional recheck. Controlled — the screen owns
 * the draft and the save.
 *
 * `variant='rejected'` (deliveries sent back): no chips — the reason is the
 * action and goes to `payload.rejectionReason`.
 *
 * `onSaveWithoutAction`: the quiet "Save without action" link shown while
 * nothing is chosen (the screen's main button saves with the action).
 * `autoFocus`: move focus into the panel when it opens from a click — not
 * when it appears while a temperature is being typed.
 * `focusOnError`: when `error` changes, focus the field that needs fixing
 * (the note for "Other" with no note, else the options or the recheck).
 */
const FoodSafetyCorrectiveActionPanel = ({
  section,
  reading,
  draft,
  onChange,
  showActions = true,
  showRecheck = false,
  saveLaterHint = true,
  error = null,
  onNoteEnter,
  onSaveWithoutAction = null,
  withoutActionLabel = FOOD_SAFETY_SUBMIT_WITHOUT_ACTION_LABEL,
  autoFocus = false,
  focusOnError = false,
  testIdPrefix = 'fs-corrective',
  headline = null,
  intro = null,
  variant = 'action',
  children,
}) => {
  const rejected = variant === 'rejected'
  const options = foodSafetyCorrectiveActionOptions(section)
  const noteRequired = foodSafetyCorrectiveNoteRequired(draft?.type)
  const recheckWording = foodSafetyRecheckWording(section)
  const recheckValue = parseFoodSafetyRecheckValue(draft?.recheckText)
  const recheckOutside =
    recheckValue != null && isFoodSafetyTempOutsideRange(recheckValue, reading)
  const nothingChosen = rejected
    ? !String(draft?.notes || '').trim()
    : !draft?.type

  const openFocusField = useRef(
    autoFocus
      ? resolveFoodSafetyCorrectiveFocusField(draft, { showActions, rejected })
      : null,
  )
  useEffect(() => {
    if (openFocusField.current) {
      focusFoodSafetyCorrectiveField(testIdPrefix, openFocusField.current)
    }
  }, [testIdPrefix])

  const latestDraft = useRef(draft)
  latestDraft.current = draft
  useEffect(() => {
    if (!focusOnError || !error) return
    focusFoodSafetyCorrectiveField(
      testIdPrefix,
      resolveFoodSafetyCorrectiveFocusField(latestDraft.current, {
        showActions,
        rejected,
      }),
    )
  }, [error, focusOnError, testIdPrefix, showActions, rejected])

  const saveWithout =
    onSaveWithoutAction && nothingChosen ? (
      <SaveWithoutRow>
        <QuietLink
          type='button'
          onClick={onSaveWithoutAction}
          data-testid={`${testIdPrefix}-save-without`}
        >
          {withoutActionLabel}
        </QuietLink>
        <QuietHint>
          {rejected
            ? 'You can add the reason later.'
            : 'You can record what was done later.'}
        </QuietHint>
      </SaveWithoutRow>
    ) : null

  // The headline follows the reading as it's typed, so it isn't live. One
  // fixed sentence is spoken when the panel appears (set after mount so
  // screen readers notice the change).
  const announcement = foodSafetyCorrectiveAnnouncement({ variant })
  const [spoken, setSpoken] = useState('')
  useEffect(() => {
    setSpoken(announcement)
  }, [announcement])

  return (
    <Panel data-testid={testIdPrefix}>
      <FoodSafetyVisuallyHidden
        role='status'
        aria-live='polite'
        data-testid={`${testIdPrefix}-announcement`}
      >
        {spoken}
      </FoodSafetyVisuallyHidden>
      <Headline data-testid={`${testIdPrefix}-headline`}>
        {headline ||
          (rejected
            ? formatFoodSafetyRejectedHeadline(reading)
            : formatFoodSafetyOutOfRangeHeadline(reading))}
      </Headline>

      {intro}

      {rejected ? (
        <>
          <PanelLabel as='label' htmlFor={`${testIdPrefix}-note`}>
            Why was it rejected?
          </PanelLabel>
          <Input.TextArea
            id={`${testIdPrefix}-note`}
            rows={2}
            value={draft?.notes || ''}
            maxLength={FOOD_SAFETY_CORRECTIVE_NOTE_MAX_LENGTH}
            placeholder='e.g. Too warm — sent back with the driver'
            onChange={e => onChange({ notes: e.target.value })}
            onKeyDown={e => {
              if (onNoteEnter && e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                onNoteEnter()
              }
            }}
            data-testid={`${testIdPrefix}-note`}
          />
          {saveWithout ||
            (saveLaterHint && nothingChosen ? (
              <QuietHint>You can save now and add the reason later.</QuietHint>
            ) : null)}
        </>
      ) : showActions ? (
        <>
          <PanelLabel id={`${testIdPrefix}-actions-label`}>
            What was done?
          </PanelLabel>
          <ChipRow
            role='group'
            aria-labelledby={`${testIdPrefix}-actions-label`}
          >
            {options.map(option => {
              const active = draft?.type === option.type
              return (
                <Chip
                  key={option.type}
                  type='button'
                  aria-pressed={active}
                  data-active={active ? 'true' : 'false'}
                  data-testid={`${testIdPrefix}-chip-${option.type}`}
                  onClick={() =>
                    onChange({ type: active ? null : option.type })
                  }
                >
                  {option.label}
                </Chip>
              )
            })}
          </ChipRow>
          <Input.TextArea
            rows={2}
            value={draft?.notes || ''}
            maxLength={FOOD_SAFETY_CORRECTIVE_NOTE_MAX_LENGTH}
            aria-label={noteRequired ? 'Note (required)' : 'Note (optional)'}
            placeholder={foodSafetyCorrectiveNotePlaceholder(section, {
              required: noteRequired,
            })}
            onChange={e => onChange({ notes: e.target.value })}
            onKeyDown={e => {
              if (onNoteEnter && e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                onNoteEnter()
              }
            }}
            data-testid={`${testIdPrefix}-note`}
          />
          {saveWithout ||
            (saveLaterHint && nothingChosen ? (
              <QuietHint>
                You can save the reading now and record corrective action later.
              </QuietHint>
            ) : null)}
        </>
      ) : null}

      {showRecheck ? (
        draft?.recheckOpen ? (
          <RecheckRow>
            <PanelLabel as='label' htmlFor={`${testIdPrefix}-recheck-input`}>
              {recheckWording.fieldLabel}
            </PanelLabel>
            <RecheckField>
              <Input
                id={`${testIdPrefix}-recheck-input`}
                value={draft?.recheckText || ''}
                onChange={e => onChange({ recheckText: e.target.value })}
                placeholder='°C'
                inputMode='decimal'
                style={{ width: '7rem' }}
                data-testid={`${testIdPrefix}-recheck-input`}
              />
              <span>°C</span>
              <QuietLink
                type='button'
                onClick={() =>
                  onChange({ recheckOpen: false, recheckText: '' })
                }
              >
                Remove recheck
              </QuietLink>
            </RecheckField>
            {recheckValue != null ? (
              <RecheckTone
                data-tone={recheckOutside ? 'fail' : 'pass'}
                data-testid={`${testIdPrefix}-recheck-tone`}
              >
                {recheckOutside
                  ? recheckWording.failLabel
                  : recheckWording.passLabel}
              </RecheckTone>
            ) : null}
          </RecheckRow>
        ) : (
          <>
            {draft?.type && !rejected ? (
              <RecheckRow>
                <PanelLabel id={`${testIdPrefix}-recheck-by-label`}>
                  {recheckWording.remindLabel}
                </PanelLabel>
                <ChipRow
                  role='group'
                  aria-labelledby={`${testIdPrefix}-recheck-by-label`}
                >
                  {FOOD_SAFETY_RECHECK_REMINDER_MINUTES.map(minutes => {
                    const active = Number(draft?.recheckInMinutes) === minutes
                    return (
                      <Chip
                        key={minutes}
                        type='button'
                        aria-pressed={active}
                        data-active={active ? 'true' : 'false'}
                        data-testid={`${testIdPrefix}-recheck-by-${minutes}`}
                        onClick={() =>
                          onChange({
                            recheckInMinutes: active ? null : minutes,
                          })
                        }
                      >
                        {minutes} min
                      </Chip>
                    )
                  })}
                </ChipRow>
                {draft?.recheckInMinutes ? (
                  <QuietHint data-testid={`${testIdPrefix}-recheck-by-hint`}>
                    Shows as a reminder on this page — no notifications.
                  </QuietHint>
                ) : null}
              </RecheckRow>
            ) : null}
            <QuietLink
              type='button'
              onClick={() => onChange({ recheckOpen: true })}
              data-testid={`${testIdPrefix}-recheck-open`}
            >
              {recheckWording.openLabel}
            </QuietLink>
          </>
        )
      ) : null}

      {error ? (
        <ErrorText role='alert' data-testid={`${testIdPrefix}-error`}>
          {error}
        </ErrorText>
      ) : null}

      {children}
    </Panel>
  )
}

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding: 0.75rem;
  border: 1px solid #f0d9b5;
  border-radius: 6px;
  background: #fffaf2;
`

const Headline = styled.p`
  margin: 0;
  font-weight: 700;
  color: ${FOOD_SAFETY_TEMP_TONE_COLOUR.fail};
`

const PanelLabel = styled.span`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.bbqBlack || '#000'};
`

const ChipRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.4rem;
`

const Chip = styled.button`
  border: 1px solid ${({ theme }) => theme.colours.panSilver || '#ddd'};
  background: #fff;
  border-radius: 999px;
  padding: 0.3rem 0.75rem;
  cursor: pointer;
  font-size: 0.85rem;

  &:hover {
    border-color: ${({ theme }) => theme.colours.lettuceGreen || '#7cb305'};
  }

  &[data-active='true'] {
    border-color: #5a9e0b;
    background: #f3f8e8;
    font-weight: 600;
  }
`

const QuietHint = styled.p`
  margin: 0;
  font-size: 0.8rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const QuietLink = styled.button`
  align-self: flex-start;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
  text-decoration: underline;
`

const SaveWithoutRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.5rem;
`

const RecheckRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
`

const RecheckField = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`

const RecheckTone = styled.span`
  font-size: 0.85rem;
  font-weight: 600;
  color: ${({ 'data-tone': tone }) => FOOD_SAFETY_TEMP_TONE_COLOUR[tone]};
`

const ErrorText = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: #a8071a;
`

export default FoodSafetyCorrectiveActionPanel
